import type { ConversationDotStatus } from "@app/lib/utils/conversation_dot_status";
import type { ModelId } from "@app/types/shared/model_id";

export const POD_TASK_STATUSES = ["todo", "in_progress", "done"] as const;

export type PodTaskStatus = (typeof POD_TASK_STATUSES)[number];

export const POD_TASK_PRIORITIES = ["P0", "P1", "P2", "P3"] as const;

export type PodTaskPriority = (typeof POD_TASK_PRIORITIES)[number];

/**
 * Whether a task's deadline is in the past. Done tasks are never overdue;
 * a missing deadline never is either. Shared by agent tool listings, the
 * task kickoff prompt, and the SPA due chips so the rule cannot drift.
 */
export function isPodTaskOverdue(
  dueAt: Date | string | null,
  status?: PodTaskStatus
): boolean {
  if (dueAt === null || status === "done") {
    return false;
  }
  return new Date(dueAt).getTime() < Date.now();
}

/**
 * Parses a due date accepted on every task create/update surface (agent tools
 * and API routes). A date-only value ("2026-10-24") means "any time during
 * that day" and resolves to the end of that day UTC; a full ISO 8601 datetime
 * is used as-is (naive datetimes are treated as UTC). Returns null for
 * anything unparseable.
 */
export function parsePodTaskDueAt(raw: string): Date | null {
  const trimmed = raw.trim();
  if (trimmed.length === 0) {
    return null;
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    const date = new Date(`${trimmed}T23:59:59.999Z`);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  const hasExplicitZone = /(?:[zZ]|[+-]\d{2}:?\d{2})$/.test(trimmed);
  const date = new Date(hasExplicitZone ? trimmed : `${trimmed}Z`);
  return Number.isNaN(date.getTime()) ? null : date;
}

export const POD_TASK_ACTOR_TYPES = ["user", "agent"] as const;

export type PodTaskActorType = (typeof POD_TASK_ACTOR_TYPES)[number];

export const POD_TASK_SOURCE_TYPES = [
  "project_conversation",
  "project_knowledge",
  "slack",
  "notion",
  "gdrive",
  "confluence",
  "github",
  "microsoft",
] as const;

export type PodTaskSourceType = (typeof POD_TASK_SOURCE_TYPES)[number];

export const AGENT_SUGGESTION_STATUSES = [
  "pending",
  "approved",
  "rejected",
] as const;
export type AgentSuggestionStatus = (typeof AGENT_SUGGESTION_STATUSES)[number];

export const POD_TASK_PERIOD_SCOPES = [
  "active",
  "last_24h",
  "last_7d",
  "last_30d",
] as const;
export type PodTaskPeriodScope = (typeof POD_TASK_PERIOD_SCOPES)[number];

export function isPodTaskPeriodScope(v: string): v is PodTaskPeriodScope {
  return POD_TASK_PERIOD_SCOPES.includes(v as PodTaskPeriodScope);
}

export const POD_TASK_PEOPLE_SCOPES = ["just_mine", "all_project"] as const;
export type PodTaskPeopleScope = (typeof POD_TASK_PEOPLE_SCOPES)[number];

export function isPodTaskPeopleScope(v: string): v is PodTaskPeopleScope {
  return POD_TASK_PEOPLE_SCOPES.includes(v as PodTaskPeopleScope);
}

export type PodTaskSourceInfo = {
  sourceType: PodTaskSourceType;
  sourceId: string;
  sourceTitle: string | null;
  sourceUrl: string | null;
};

export type PodTaskAssigneeType = {
  sId: string;
  fullName: string;
  image: string | null;
};

/** Stable row / stable-order key when grouping tasks with no assignee. */
export const POD_TASK_UNASSIGNED_GROUP_KEY = "__unassigned__";

/** Header and menu copy for tasks with no assignee. */
export const POD_TASK_NO_ASSIGNEE_LABEL = "No assignee";

/** Seeded via POST /pods/:podId/tasks/seed (editors only). */
export const POD_MANAGER_AGENT_SID = "pod_manager" as const;

export type PodTaskType = {
  id: ModelId;
  sId: string;
  user: PodTaskAssigneeType | null;
  conversationId: string | null;
  /** Same semantics as the left sidebar conversation row (see `getConversationDotStatus`). */
  conversationSidebarStatus: ConversationDotStatus | null;
  conversationIsRunningAgentLoop: boolean | null;
  text: string;
  status: PodTaskStatus;
  /** Deadline; date-only inputs resolve to the end of that day UTC (see parsePodTaskDueAt). */
  dueAt: Date | null;
  /** P0 is the highest priority. */
  priority: PodTaskPriority | null;
  doneAt: Date | null;
  /** Optional persisted instructions merged into the kickoff prompt when the todo is started. */
  agentInstructions: string | null;
  actorRationale: string | null;
  createdByType: PodTaskActorType;
  createdByAgentConfigurationId: string | null;
  createdByUserId: string | null;
  agentSuggestionStatus: AgentSuggestionStatus | null;
  agentSuggestionReviewedAt: Date | null;
  markedAsDoneByType: PodTaskActorType | null;
  markedAsDoneByAgentConfigurationId: string | null;
  markedAsDoneByUserId: string | null;
  sources: PodTaskSourceInfo[];
  createdAt: Date;
  updatedAt: Date;
};
